import { useMemo } from "react";
import { useAllItems, useAllPhases, useProjects, useAllSubtasks, subtaskCounts } from "@/hooks/useData";
import { useTaskEditor } from "@/context/TaskEditorContext";
import { useIsMobile } from "@/lib/useMediaQuery";
import type { Phase, Project } from "@/lib/types";
import { GanttTimeline } from "@/components/GanttTimeline";
import { progressCounts } from "@/lib/hierarchy";
import { useChrome } from "@/context/ChromeContext";

// The Roadmap is one Gantt across every project: phases (grouped by project,
// then phase order) down the left, work on a shared week grid. Same treatment
// as a project's own Timeline tab — see components/GanttTimeline.
export function Roadmap() {
  const { data: items = [] } = useAllItems();
  const { data: phases = [] } = useAllPhases();
  const { data: projects = [] } = useProjects();
  const { data: subs = [] } = useAllSubtasks();
  const { openItem } = useTaskEditor();
  const mobile = useIsMobile();

  const subCounts = useMemo(() => progressCounts(items, subtaskCounts(subs)), [items, subs]);
  const projectById = useMemo(() => new Map(projects.map((p: Project) => [p.id, p])), [projects]);

  // Order phases by their project's position in the list, then phase.position,
  // so each project's phases stay contiguous and in sequence.
  const orderedPhases = useMemo(() => {
    const projOrder = new Map<string, number>(projects.map((p: Project, i) => [p.id, i]));
    return [...phases].sort((a: Phase, b: Phase) => {
      const pa = projOrder.get(a.project) ?? 999;
      const pb = projOrder.get(b.project) ?? 999;
      return pa - pb || a.position - b.position;
    });
  }, [phases, projects]);

  useChrome(
    { title: "Roadmap", subtitle: "All phases on one timeline" },
    []
  );

  return (
    <div style={{ padding: mobile ? "16px 16px 90px" : "20px 36px 90px" }}>
      <GanttTimeline
        phases={orderedPhases}
        items={items}
        subCounts={subCounts}
        onOpen={openItem}
        mobile={mobile}
        projectOf={(ph) => projectById.get(ph.project)}
      />
    </div>
  );
}
